import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import logo from "./assets/logo.jpeg";

const API_BASE_URL = 'https://omapi.omgoldfinance.live/api';

// Create axios instance with credentials
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

const Login = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      setError("Please enter email and password");
      return;
    }

    setError("");
    setInfoMessage("");
    setLoading(true);

    try {
      // Call main login endpoint (checks both Admin and Employee tables)
      console.log("Attempting login...");
      let response;
      try {
        response = await api.post("/login", {
          email: email,
          password: password,
        });
      } catch (firstErr) {
        // If /login is not found (404), try /auth/login as fallback
        if (firstErr.response && firstErr.response.status === 404) {
          response = await api.post("/auth/login", {
            email: email,
            password: password,
          });
        } else {
          throw firstErr;
        }
      }

      const responseData = response.data;
      console.log("Login successful:", responseData);

      const userData = responseData.user || responseData;
      const userToStore = {
        id: userData.id || userData.user_id,
        employee_id: userData.employee_id || userData.emp_id || null,
        full_name: userData.full_name || userData.name || userData.username,
        email: userData.email,
        user_type: userData.user_type || userData.role || "user",
        department: userData.department || null,
        designation: userData.designation || null,
        current_company: userData.current_company || userData.company || null,
        phone_number: userData.phone_number || userData.phone || null,
        blood_group: userData.blood_group || null,
        permissions: userData.permissions || {}
      };

      if (responseData.access_token) {
        localStorage.setItem("token", responseData.access_token);
      }
      localStorage.setItem("user", JSON.stringify(userToStore));
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("loginType", userData.user_type || "standard");

      if (userData.user_type) {
        localStorage.setItem("userType", userData.user_type);
      }
      if (userData.current_company) {
        localStorage.setItem("company", userData.current_company);
      }

      setLoading(false);
      navigate("/dashboard");

    } catch (err) {
      console.error("Login error:", err);
      if (err.response && err.response.data && err.response.data.error) {
        setError(err.response.data.error);
      } else if (err.response && err.response.status === 401) {
        setError("Invalid email or password.");
      } else if (err.request) {
        setError("Server error. Please check your connection and try again.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    setError("");
    setInfoMessage("Please contact the administrator to reset your password.");
  };

  return (
    <div style={styles.container}>
      <form style={styles.form} onSubmit={handleSubmit}>
        <div style={styles.logoContainer}>
          <img src={logo} alt="Avva Inventory Logo" style={styles.logo} />
        </div>
        <h2 style={styles.title}></h2>
        <h2 style={styles.title}></h2>
        <p style={styles.subtitle}>Please login to continue</p>

        {error && <p style={styles.error}>{error}</p>}
        {infoMessage && <p style={styles.info}>{infoMessage}</p>}

        <div style={styles.inputGroup}>
          <label style={styles.label}>Email</label>
          <input
            type="email"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={styles.input}
          />
        </div>

        <div style={styles.inputGroup}>
          <label style={styles.label}>Password</label>

          <div style={styles.passwordWrapper}>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={styles.passwordInput}
            />
            <span
              style={styles.eyeIcon}
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
        </div>

        <div style={styles.forgotWrapper}>
          <span style={styles.forgot} onClick={handleForgotPassword}>
            Forgot Password?
          </span>
        </div>

        <button type="submit" style={styles.button} disabled={loading}>
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>
    </div>
  );
};

const styles = {
  container: {
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "linear-gradient(135deg, #0f2027, #203a43, #2c5364)",
  },
  form: {
    background: "#1e1e2f",
    padding: "35px",
    borderRadius: "12px",
    width: "360px",
    boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
    color: "#fff",
  },
  title: {
    textAlign: "center",
    marginBottom: "5px",
    marginTop: "10px",
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  logo: {
    width: "120px",
    height: "auto",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.3)",
  },
  subtitle: {
    textAlign: "center",
    fontSize: "14px",
    marginBottom: "20px",
    color: "#aaa",
  },
  inputGroup: {
    marginBottom: "18px",
    display: "flex",
    flexDirection: "column",
  },
  label: {
    fontSize: "14px",
    marginBottom: "6px",
  },
  input: {
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #444",
    backgroundColor: "#2a2a40",
    color: "#fff",
    outline: "none",
  },
  passwordWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  passwordInput: {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #444",
    backgroundColor: "#2a2a40",
    color: "#fff",
    outline: "none",
  },
  eyeIcon: {
    position: "absolute",
    right: "10px",
    cursor: "pointer",
    color: "#aaa",
  },
  forgotWrapper: {
    textAlign: "right",
    marginBottom: "15px",
  },
  forgot: {
    fontSize: "13px",
    color: "#4da6ff",
    cursor: "pointer",
  },
  button: {
    width: "100%",
    padding: "12px",
    backgroundColor: "#4da6ff",
    color: "white",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontWeight: "bold",
    transition: "0.3s",
  },
  error: {
    color: "#ff4d4d",
    fontSize: "14px",
    marginBottom: "10px",
  },
  info: {
    color: "#4da6ff",
    fontSize: "14px",
    marginBottom: "10px",
  },
};

export default Login;